<script lang="ts">
  import { onMount } from 'svelte';
  import { installState } from '../lib/install.svelte';
  import { router } from '../lib/router.svelte';

  // Pages where a gentle prompt helps; never over the booking form or admin.
  const BANNER_PATHS = ['/', '/packages', '/about', '/reviews', '/booking/status'];
  const BANNER_DELAY_MS = 4000;

  let ready = $state(false);
  let dismissed = $state(installState.dismissedRecently());
  let copied = $state(false);
  let dialog = $state<HTMLDivElement | null>(null);
  // The marketing-consent banner uses the same spot; wait until it's answered.
  let consentOpen = $state(false);

  const path = $derived(router.state.path);
  const platform = $derived(installState.platform);
  const showBanner = $derived(
    ready && !dismissed && !consentOpen && !installState.installed && !installState.helpOpen &&
    platform !== 'manual' &&
    (BANNER_PATHS.includes(path) || path.startsWith('/packages/')),
  );

  onMount(() => {
    const timer = setTimeout(() => { ready = true; }, BANNER_DELAY_MS);
    const checkConsent = () => { consentOpen = Boolean(document.querySelector('.consent-banner')); };
    checkConsent();
    const observer = new MutationObserver(checkConsent);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearTimeout(timer); observer.disconnect(); };
  });

  // Lift the floating Messenger button above the banner while it shows.
  $effect(() => {
    document.body.classList.toggle('install-banner-open', showBanner);
    return () => document.body.classList.remove('install-banner-open');
  });

  $effect(() => { if (installState.helpOpen) dialog?.focus(); });

  function close() {
    dismissed = true;
    installState.dismiss();
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      copied = true;
      setTimeout(() => { copied = false; }, 2500);
    } catch { /* the address is shown on screen to copy by hand */ }
  }
</script>

{#if showBanner}
  <div class="install-banner" role="dialog" aria-label="Get the Bibow app">
    <img src="/icon-192.png" alt="" width="48" height="48" />
    <div class="install-copy">
      <strong>Get the Bibow app</strong>
      <span>Book and check your booking in one tap from your home screen.</span>
    </div>
    <button type="button" class="btn btn-bibow btn-sm px-3" onclick={() => installState.start()}>{platform === 'prompt' ? 'Install' : 'How?'}</button>
    <button type="button" class="install-close" aria-label="Not now" onclick={close}>×</button>
  </div>
{/if}

{#if installState.helpOpen}
  <div class="install-overlay" role="presentation" onclick={(e) => { if (e.target === e.currentTarget) installState.helpOpen = false; }}>
    <div class="install-dialog" role="dialog" aria-modal="true" aria-labelledby="install-title" tabindex="-1" bind:this={dialog} onkeydown={(e) => { if (e.key === 'Escape') installState.helpOpen = false; }}>
      <div class="d-flex align-items-center gap-3 mb-3">
        <img src="/icon-192.png" alt="" width="52" height="52" />
        <div><h2 id="install-title" class="h5 mb-0">Add Bibow to your home screen</h2><small class="text-secondary">Free · no app store needed</small></div>
      </div>
      {#if platform === 'in-app'}
        <p>Messenger and Facebook can't install apps. Open this page in your phone's browser first:</p>
        <ol>
          <li>Tap <b>⋯</b> or <b>⋮</b> at the top right.</li>
          <li>Choose <b>Open in browser</b> (Chrome on Android, Safari on iPhone).</li>
          <li>Tap <b>Get the Bibow app</b> again there.</li>
        </ol>
        <div class="input-group mt-2"><input class="form-control" readonly value={window.location.origin} aria-label="Website address" /><button type="button" class="btn btn-dark" onclick={copyLink}>{copied ? 'Copied ✓' : 'Copy'}</button></div>
      {:else if platform === 'ios'}
        <ol>
          <li>Open this page in <b>Safari</b>.</li>
          <li>Tap the <b>Share</b> button <span class="share-icon" aria-hidden="true">⬆︎</span> at the bottom of the screen.</li>
          <li>Scroll down and tap <b>Add to Home Screen</b>, then <b>Add</b>.</li>
        </ol>
      {:else}
        <p>Open your browser's menu (<b>⋮</b>) and choose <b>Install app</b> or <b>Add to Home screen</b>.</p>
      {/if}
      <button type="button" class="btn btn-light w-100 mt-3" onclick={() => (installState.helpOpen = false)}>Close</button>
    </div>
  </div>
{/if}

<style>
.install-banner{position:fixed;left:12px;right:12px;bottom:12px;z-index:1070;display:flex;align-items:center;gap:.75rem;max-width:560px;margin:0 auto;padding:.75rem .75rem .75rem .85rem;background:#fff;border-radius:1.1rem;box-shadow:0 10px 30px rgba(15,23,32,.18);animation:install-in .25s ease-out}
.install-banner img{border-radius:.8rem;flex:0 0 auto}
.install-copy{display:flex;flex-direction:column;min-width:0;line-height:1.25}
.install-copy span{font-size:.82rem;color:#6b7280}
.install-banner .btn{flex:0 0 auto;margin-left:auto}
.install-close{flex:0 0 auto;border:0;background:none;font-size:1.4rem;line-height:1;color:#9aa1a8;padding:.25rem .35rem}
.install-overlay{position:fixed;inset:0;z-index:1080;background:rgba(15,23,32,.55);display:flex;align-items:flex-end;justify-content:center;padding:12px}
.install-dialog{width:min(460px,100%);background:#fff;border-radius:1.25rem;padding:1.25rem 1.25rem 1rem;outline:none}
.install-dialog img{border-radius:.8rem}
.install-dialog ol{padding-left:1.2rem;margin-bottom:0}
.install-dialog li{margin:.35rem 0}
.share-icon{display:inline-block;border:1.5px solid currentColor;border-radius:.3rem;padding:0 .2rem;font-size:.8rem}
@media (min-width:768px){.install-overlay{align-items:center}}
@keyframes install-in{from{transform:translateY(20px);opacity:0}to{transform:none;opacity:1}}
@media (prefers-reduced-motion:reduce){.install-banner{animation:none}}
</style>
